import { isUndefined, merge } from 'lodash-es';

import { isValidColorRule } from '../../core/slots/ColorRule.js';
import type { ChartToolbarProps } from '../../core/types/toolbar.js';
import { defaultUnitFormatter } from '../../core/utils/defaultUnitFormatter.js';
import { isValidLegendPosition } from '../../core/utils/is-valid-legend-position.js';
import { defaultTreeMapConfig } from '../defaults.js';
import type {
  TreemapChartLegendConfig,
  TreeMapChartSlots,
  TreeMapChartToolbarConfig,
  TreeMapConfig,
  TreeMapContextConfig,
} from '../types/treemap-chart.config.js';
import type { TreeMapHierarchicalProps } from '../types/treemap.js';

const validateToolbarContextConfig = (
  contextConfig: TreeMapChartToolbarConfig | undefined,
  // oxlint-disable-next-line typescript/no-deprecated
) => !isUndefined(contextConfig) && contextConfig.downloadData?.enabled;

const getToolbarConfiguration = (
  contextConfig?: TreeMapChartToolbarConfig,
  slots?: Partial<ChartToolbarProps>,
) => {
  const hasValidToolbarConfig =
    validateToolbarContextConfig(contextConfig) || !isUndefined(slots);

  return hasValidToolbarConfig ? merge({}, contextConfig, slots) : undefined;
};

/**
 * Builds a LegendConfiguration based on context and slots
 *
 * @param contextLegendConfig - legend config context
 * @param slotsLegendConfig - legend config props
 */
export function getLegendConfiguration(
  contextLegendConfig?: TreemapChartLegendConfig,
  slotsLegendConfig?: TreemapChartLegendConfig,
): TreemapChartLegendConfig {
  let legend = merge(
    {},
    defaultTreeMapConfig.legend,
    contextLegendConfig,
    slotsLegendConfig,
  );

  const position =
    legend.position && isValidLegendPosition(legend.position)
      ? legend.position
      : defaultTreeMapConfig.legend.position;

  legend = {
    ...legend,
    position,
  };

  return legend;
}

/**
 * Build treemap chart configuration.
 *
 * @param props - config from props
 * @param config - config from context
 * @param slots - config from components slots
 * @returns TreeMapConfig
 */
export const buildTreeMapChartConfig = (
  // oxlint-disable-next-line typescript/no-deprecated
  props: TreeMapHierarchicalProps,
  config: TreeMapContextConfig,
  slots: TreeMapChartSlots,
): TreeMapConfig => {
  const colorPalette =
    props.colorPalette ??
    config.colorPalette ??
    defaultTreeMapConfig.colorPalette;

  const formatter =
    props.formatter ??
    config.formatter ??
    defaultUnitFormatter(props.unit ?? props.data?.unit);

  const truncationMode =
    props.truncationMode ??
    config.truncationMode ??
    defaultTreeMapConfig.truncationMode;

  const loading =
    props.loading ?? config.loading ?? defaultTreeMapConfig.loading;

  const labelsDisplay =
    props.labelsDisplay ??
    config.labelsDisplay ??
    defaultTreeMapConfig.labelsDisplay;

  const nameAccessor =
    props.nameAccessor ??
    config.nameAccessor ??
    defaultTreeMapConfig.nameAccessor;

  const min = props.min ?? config.min;
  const max = props.max ?? config.max;

  const legend = getLegendConfiguration(config.legend, slots.legend);

  const toolbar = getToolbarConfiguration(config.toolbar, slots.toolbar);

  const colorRules = (config.colorRules ?? [])
    .filter(isValidColorRule)
    .concat(slots.colorRules?.filter(isValidColorRule) ?? []);

  const textSize =
    props.textSize ?? config.textSize ?? defaultTreeMapConfig.textSize;

  return {
    colorPalette,
    formatter,
    truncationMode,
    loading,
    legend,
    toolbar,
    labelsDisplay,
    nameAccessor,
    min,
    max,
    colorRules,
    textSize,
    tooltip: slots.tooltip ?? config.tooltip,
  };
};
